// Persona-key derivation for SME onboarding (WP1b).
//
// This is the persona<->roles mapping historically kept private inside
// TopBar.jsx (`personaKeyFromRoles`). Multiple onboarding work packages need to
// resolve a persona key from the current user's roles without importing the
// topbar, so the logic is extracted here verbatim. TopBar retains its own copy;
// this module must stay behaviorally identical to it.
//
// Precedence and fallthrough exactly mirror the original TopBar implementation:
//   executive_sponsor > delivery_leader > data_steward > system_admin
//   any other authenticated role set -> pending_access
//   non-array input -> null
import { rbac } from '@dha-nexus/shared';

// The five persona keys the login/topbar surfaces recognize. Frozen so callers
// can treat it as a stable enum. Order matches rbac.PERSONA_DISPLAY.
export const PERSONA_KEYS = Object.freeze([
  'executive_sponsor',
  'delivery_leader',
  'data_steward',
  'pending_access',
  'system_admin',
]);

/**
 * Map an actor's roles array to a persona key.
 *
 * @param {string[]} roles - role ids (e.g. rbac.ROLE_IDS.*)
 * @returns {('executive_sponsor'|'delivery_leader'|'data_steward'|'system_admin'|'pending_access'|null)}
 *   the persona key, or null when `roles` is not an array.
 */
export function personaKeyFromRoles(roles) {
  if (!Array.isArray(roles)) return null;
  if (roles.includes(rbac.ROLE_IDS.EXECUTIVE_SPONSOR)) return 'executive_sponsor';
  if (
    roles.includes(rbac.ROLE_IDS.PORTFOLIO_MANAGER) ||
    roles.includes(rbac.ROLE_IDS.PROGRAM_MANAGER)
  ) {
    return 'delivery_leader';
  }
  if (roles.includes(rbac.ROLE_IDS.DATA_STEWARD)) return 'data_steward';
  if (roles.includes(rbac.ROLE_IDS.SYSTEM_ADMIN)) return 'system_admin';
  return 'pending_access';
}

// Acronyms/compounds that plain title-casing would mangle.
const ROLE_WORD_OVERRIDES = {
  SME: 'SME',
  AI: 'AI',
  IMO: 'IMO',
  RBAC: 'RBAC',
  READONLY: 'Read-only',
};

/**
 * Human label for a role id: 'ROLE-PROGRAM-MANAGER' → 'Program Manager'.
 * Role-aware surfaces must name the account's ACTUAL role, not just the
 * persona bucket it maps to (DEF-002).
 *
 * @param {string} roleId
 * @returns {string|null}
 */
export function roleDisplayName(roleId) {
  const raw = String(roleId || '').replace(/^ROLE-/, '');
  if (!raw) return null;
  return raw
    .split('-')
    .map((word) => ROLE_WORD_OVERRIDES[word] || word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * The display label for an actor's primary (first) role, or null when the
 * roles array is empty/absent.
 *
 * @param {string[]} roles
 * @returns {string|null}
 */
export function primaryRoleLabel(roles) {
  if (!Array.isArray(roles) || !roles.length) return null;
  return roleDisplayName(roles[0]);
}
